import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { SupplierAccess } from "@shared-core/data/supplier-portal-adapter";
import { useSupplierPortalAdapter } from "@shared-core/data/SupplierPortalAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { linkTokenFromFragment } from "@shared-core/domain/supplier-link-fragment";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { useLoad } from "@shared-core/domain/useLoad";
import {
  ACTION_WORDING,
  PAGE_WORDING,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { LinkShell } from "../shell/LinkShell";
import { SignedInShell } from "../shell/SignedInShell";
import { useSupplierSession } from "../shell/session";
import { useIsStaffView, useNotFoundWhy } from "../shell/staffView";
import { EmptyState, LoadFailed, LoadingCard } from "../shell/states";
import { PurchaseOrderBody } from "./PurchaseOrderBody";

/**
 * `/po/:purchaseOrderNo`: one purchase order, two ways in (brief §9). With an emailed link's token in the #fragment
 * it opens in the link chrome, scoped to this order alone; without one it opens inside the signed-in shell.
 */
export function PurchaseOrderPage() {
  const { purchaseOrderNo = "" } = useParams<{ purchaseOrderNo: string }>();
  const { hash, pathname } = useLocation();
  // A staff view is never a link: a pasted #fragment must not draw the link chrome (spec 05 §4.5).
  const isStaffView = useIsStaffView();
  const token = isStaffView ? null : linkTokenFromFragment(hash);

  if (token) {
    return (
      <LinkShell returnTo={pathname}>
        <PurchaseOrderView
          access={{ via: "link", token }}
          purchaseOrderNo={purchaseOrderNo}
        />
      </LinkShell>
    );
  }
  return (
    <SignedInShell>
      <PurchaseOrderView
        access={{ via: "login" }}
        purchaseOrderNo={purchaseOrderNo}
      />
    </SignedInShell>
  );
}

function PurchaseOrderView({
  access: accessProp,
  purchaseOrderNo,
}: {
  access: SupplierAccess;
  purchaseOrderNo: string;
}) {
  const adapter = useSupplierPortalAdapter();
  const capabilities = usePlatformCapabilities();
  const navigate = useNavigate();
  const signedIn = useSupplierSession();
  const notFoundWhy = useNotFoundWhy("order", PAGE_WORDING.notFoundWhy);
  const accessKey =
    accessProp.via === "link" ? `link:${accessProp.token}` : "login";
  // One access object per credential, so the read's key and the writes agree.
  const access = useMemo(() => accessProp, [accessKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const {
    data: read,
    error,
    reload,
  } = useLoad(
    () => adapter.getPurchaseOrder(access, purchaseOrderNo),
    `${accessKey}|${purchaseOrderNo}`,
  );

  useEffect(() => {
    if (read?.kind === "signed_out") {
      navigate(
        paths.supplierSignIn({
          returnTo: paths.supplierPurchaseOrder({ purchaseOrderNo }),
        }),
        {
          replace: true,
        },
      );
    }
  }, [read, navigate, purchaseOrderNo]);

  if (!read) {
    return error ? (
      <LoadFailed
        what="this purchase order"
        message={mutationErrorMessage(
          error,
          "Something went wrong reaching Pollards.",
        )}
        onRetry={reload}
      />
    ) : (
      <LoadingCard what="the purchase order" />
    );
  }

  if (read.kind === "link_not_valid") {
    return (
      <Card>
        <EmptyState
          title={PAGE_WORDING.linkNotValidTitle}
          action={
            <Button asChild>
              <Link to={paths.supplierSignIn()}>{ACTION_WORDING.signIn}</Link>
            </Button>
          }
        >
          <p>{PAGE_WORDING.linkNotValidWhy}</p>
          <p>{PAGE_WORDING.linkNotValidNext}</p>
        </EmptyState>
      </Card>
    );
  }
  if (read.kind === "not_found") {
    return (
      <Card>
        <EmptyState
          title={PAGE_WORDING.notFoundTitle}
          action={
            <Button asChild variant="outline">
              <Link to={paths.supplierOrders()}>Your orders</Link>
            </Button>
          }
        >
          <p>{notFoundWhy}</p>
        </EmptyState>
      </Card>
    );
  }
  if (read.kind === "signed_out")
    return <LoadingCard what="the purchase order" />;

  // After a write: read the order again, and the session's counts with it.
  const afterWrite = async <T,>(write: Promise<T>): Promise<T> => {
    const result = await write;
    reload();
    signedIn?.refresh();
    return result;
  };
  const no = read.page.purchase_order_no;

  return (
    <PurchaseOrderBody
      page={read.page}
      via={access.via}
      writes={{
        accept: (lineNo) =>
          afterWrite(adapter.acceptLines(access, no, [lineNo])),
        acceptAll: (lineNos) =>
          afterWrite(adapter.acceptLines(access, no, lineNos)),
        acceptAtPrice: (lineNo, costPence) =>
          afterWrite(
            adapter.acceptLinesAtPrice(access, no, [
              { line_no: lineNo, cost_pence: costPence },
            ]),
          ),
        proposeDate: (lineNo, input) =>
          afterWrite(adapter.proposeDate(access, no, lineNo, input)),
        reject: (lineNo, reason) =>
          afterWrite(adapter.rejectLine(access, no, lineNo, reason)),
        answerCancellation: (lineNo, input) =>
          afterWrite(adapter.answerCancellation(access, no, lineNo, input)),
        answerAmendment: (lineNo, input) =>
          afterWrite(adapter.answerAmendment(access, no, lineNo, input)),
        despatch: (lineNo, input) =>
          afterWrite(adapter.markDespatched(access, no, lineNo, input)),
        uploadProof: (lineNo, file) =>
          afterWrite(adapter.uploadProof(access, no, lineNo, file)),
        reportShortage: (input) =>
          afterWrite(adapter.reportShortage(access, no, input)),
        uploadInvoice: (input) =>
          afterWrite(adapter.uploadInvoice(access, input)),
        replyToQuery: (invoiceRef, input) =>
          afterWrite(adapter.replyToInvoiceQuery(access, invoiceRef, input)),
        kitDrops: (dropsUrl, options) =>
          adapter.getKitDrops(access, dropsUrl, options),
        downloadManifest: async (manifestUrl) => {
          const { file_name, blob } = await adapter.downloadFile(
            access,
            manifestUrl,
          );
          capabilities.downloadBlob(file_name, blob);
        },
        download: async (files) => {
          // Every file is tried; the line shows the failure if any didn't arrive.
          const results = await Promise.allSettled(
            files.map(async (file) => {
              const { file_name, blob } = await adapter.downloadFile(
                access,
                file.download_url,
              );
              capabilities.downloadBlob(file_name, blob);
            }),
          );
          const failed = results.filter((r) => r.status === "rejected");
          if (failed.length > 0) {
            const reason = (failed[0] as PromiseRejectedResult).reason;
            console.error("supplier_portal.file.download_failed", {
              purchase_order_no: no,
              failed_count: failed.length,
              exc_type: reason instanceof Error ? reason.name : typeof reason,
            });
            throw new Error("download_failed");
          }
        },
      }}
    />
  );
}
